if (!customElements.get('anniversary-balloons')) {
  customElements.define(
    'anniversary-balloons',
    class AnniversaryBalloons extends HTMLElement {
      connectedCallback() {
        this.storageKey = this.dataset.storageKey || 'staleks:anniversary-balloons';
        this.testMode = this.dataset.testMode === 'true';
        this.designMode = this.hasAttribute('data-design-mode');
        this.duration = (parseInt(this.dataset.duration, 10) || 5) * 1000;
        this.count = parseInt(this.dataset.count, 10) || 28;
        this.imageSrc = this.dataset.image || '';
        this.minSizePx = parseInt(this.dataset.minSize, 10) || 64;
        this.maxSizePx = parseInt(this.dataset.maxSize, 10) || 104;
        this.mascotEnabled = this.dataset.enableMascot === 'true';
        this.mascotImage = this.dataset.mascotImage || '';
        this.mascotDuration = (parseInt(this.dataset.mascotDuration, 10) || 3) * 1000;
        this.mascotWidth = parseInt(this.dataset.mascotWidth, 10) || 200;
        this.mascotTransitionMs = 700;
        this.active = false;
        this.hideTimer = null;
        this.mascotHideTimer = null;
        this.mascotLoopTimer = null;
        this.spawned = [];
        this.mascotEl = this.querySelector('.anniversary-balloons__mascot');

        this.mobileMq = window.matchMedia('(max-width: 767px)');
        this.applyResponsiveVars();

        if (!this.imageSrc && !this.mascotEnabled) return;

        if (this.shouldSkip()) {
          this.remove();
          return;
        }

        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => this.start(), { once: true });
        } else {
          this.start();
        }
      }

      disconnectedCallback() {
        if (this.hideTimer) clearTimeout(this.hideTimer);
        if (this.mascotHideTimer) clearTimeout(this.mascotHideTimer);
        if (this.mascotLoopTimer) clearTimeout(this.mascotLoopTimer);
        this.spawned.forEach((el) => el.remove());
        this.stopMascot();
      }

      isMobile() {
        return this.mobileMq?.matches ?? window.innerWidth <= 767;
      }

      getEffectiveSettings() {
        if (this.isMobile()) {
          return {
            count: Math.max(8, Math.round(this.count * 0.45)),
            minSize: Math.round(this.minSizePx * 0.7),
            maxSize: Math.round(this.maxSizePx * 0.7),
            mascotWidth: Math.round(this.mascotWidth * 0.65),
            driftMax: 48,
          };
        }
        return {
          count: this.count,
          minSize: this.minSizePx,
          maxSize: this.maxSizePx,
          mascotWidth: this.mascotWidth,
          driftMax: 80,
        };
      }

      applyResponsiveVars() {
        const s = this.getEffectiveSettings();
        this.effective = s;
        if (this.mascotEl) {
          this.style.setProperty('--ab-mascot-width', `${s.mascotWidth}px`);
        }
      }

      shouldSkip() {
        if (this.testMode || this.designMode) return false;
        const stored = this.getStorage();
        return stored && stored.shown === true;
      }

      getStorageUtils() {
        return window.FoxTheme?.utils || null;
      }

      getStorage() {
        const utils = this.getStorageUtils();
        if (utils?.getStorage) return utils.getStorage(this.storageKey);
        try {
          const raw = localStorage.getItem(this.storageKey);
          if (!raw) return null;
          const item = JSON.parse(raw);
          if (item.expiry && Date.now() > item.expiry) {
            localStorage.removeItem(this.storageKey);
            return null;
          }
          return item.value;
        } catch (e) {
          return null;
        }
      }

      setStorageShown() {
        if (this.testMode || this.designMode) return;
        const utils = this.getStorageUtils();
        if (utils?.setStorage) {
          utils.setStorage(this.storageKey, { shown: true }, 1);
          return;
        }
        try {
          localStorage.setItem(
            this.storageKey,
            JSON.stringify({
              value: { shown: true },
              expiry: Date.now() + 86400000,
            })
          );
        } catch (e) {
          /* ignore */
        }
      }

      start() {
        if (this.active) return;
        this.active = true;
        this.removeAttribute('hidden');
        this.classList.add('is-active');
        this.applyResponsiveVars();
        this.setStorageShown();

        if (this.mascotEnabled && this.mascotEl) {
          this.startMascot();
        }

        if (!this.imageSrc) return;

        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const settings = this.getEffectiveSettings();
        let count = settings.count;
        if (reducedMotion) {
          count = Math.min(count, 6);
        }

        const maxSpawnDelay = reducedMotion ? 0 : this.duration * 0.35;
        const maxAnimJitterMs = 1200;

        for (let i = 0; i < count; i += 1) {
          const delay = reducedMotion ? 0 : Math.random() * maxSpawnDelay;
          setTimeout(() => this.spawnBalloon(reducedMotion, settings), delay);
        }

        const hideAfterMs =
          maxSpawnDelay + this.duration + maxAnimJitterMs + 1200;
        this.hideTimer = setTimeout(() => this.finishBalloons(), hideAfterMs);
      }

      startMascot() {
        this.stopMascot();
        this.runMascotCycle();
      }

      runMascotCycle() {
        if (!this.mascotEl || !this.isConnected) return;

        const el = this.mascotEl;
        el.removeAttribute('hidden');
        el.classList.remove('is-hiding');

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            el.classList.add('is-visible');
          });
        });

        clearTimeout(this.mascotHideTimer);
        this.mascotHideTimer = setTimeout(() => this.hideMascot(), this.mascotDuration);
      }

      hideMascot() {
        if (!this.mascotEl) return;

        const el = this.mascotEl;
        el.classList.remove('is-visible');
        el.classList.add('is-hiding');

        clearTimeout(this.mascotLoopTimer);
        this.mascotLoopTimer = setTimeout(() => {
          el.setAttribute('hidden', '');
          el.classList.remove('is-hiding');

          if ((this.testMode || this.designMode) && this.isConnected) {
            this.mascotLoopTimer = setTimeout(() => this.runMascotCycle(), 600);
          } else if (!this.testMode && !this.designMode) {
            this.teardown();
          }
        }, this.mascotTransitionMs);
      }

      stopMascot() {
        clearTimeout(this.mascotHideTimer);
        clearTimeout(this.mascotLoopTimer);
        if (!this.mascotEl) return;
        this.mascotEl.classList.remove('is-visible', 'is-hiding');
        this.mascotEl.setAttribute('hidden', '');
      }

      spawnBalloon(reducedMotion, settings) {
        if (!this.isConnected || !this.imageSrc) return;

        const cfg = settings || this.getEffectiveSettings();
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'anniversary-balloons__item';
        btn.setAttribute('aria-label', 'Pop balloon');

        const size = this.randomBetween(cfg.minSize, cfg.maxSize);
        const left = this.randomBetween(2, 92);
        const drift = this.randomBetween(-cfg.driftMax, cfg.driftMax);
        const rotateStart = this.randomBetween(-14, 14);
        const rotateEnd = this.randomBetween(-18, 18);
        const durationSec = reducedMotion
          ? Math.max(2.5, this.duration / 1000 * 0.4)
          : this.duration / 1000 + this.randomBetween(-0.8, 1.2);

        btn.style.width = `${size}px`;
        btn.style.left = `${left}%`;
        btn.style.setProperty('--ab-drift', `${drift}px`);
        btn.style.setProperty('--ab-rotate-start', `${rotateStart}deg`);
        btn.style.setProperty('--ab-rotate-end', `${rotateEnd}deg`);
        btn.style.animation = `anniversary-balloon-float ${durationSec}s ease-out forwards`;

        const img = document.createElement('img');
        img.src = this.imageSrc;
        img.alt = '';
        img.width = size;
        img.height = Math.round(size * 1.15);
        img.loading = 'eager';
        img.decoding = 'async';
        btn.appendChild(img);

        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.popBalloon(btn);
        });

        this.appendChild(btn);
        this.spawned.push(btn);
      }

      popBalloon(btn) {
        if (btn.classList.contains('is-popped')) return;
        btn.classList.add('is-popped');
        btn.style.animation = 'none';
        setTimeout(() => {
          btn.remove();
          this.spawned = this.spawned.filter((el) => el !== btn);
        }, 450);
      }

      finishBalloons() {
        this.spawned.forEach((el) => el.remove());
        this.spawned = [];

        if (this.testMode || this.designMode) {
          this.active = false;
          clearTimeout(this.hideTimer);
          this.hideTimer = setTimeout(() => {
            if (this.isConnected) this.start();
          }, 1200);
          return;
        }

        if (!this.mascotEnabled || !this.mascotEl) {
          this.teardown();
        }
      }

      teardown() {
        this.classList.remove('is-active');
        this.setAttribute('hidden', '');
        this.stopMascot();
        if (!this.testMode && !this.designMode) {
          this.remove();
        }
      }

      randomBetween(min, max) {
        return Math.random() * (max - min) + min;
      }
    }
  );
}
